import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import {
  getTransportClasses,
  TRANSPORT_CLASS_TO_I18N,
  TRAIN,
} from 'Components/widgets/transport-details/transportUtils';
import { themes } from 'Components/widgets/be-transport-details/BeTransportDetails.themes';
import { useFeatureFlip } from 'Src/hooks/use-feature-flip/useFeatureFlip';
import { calculateTotalEmissions } from 'Utils/transport/carbonEmissionsUtils';
import { makeWidgetProps } from 'Src/types/widget-helpers';
type TransportDetailsTransportInfosProps = {
  theme: 'mobile' | 'desktop';
  transportDetailEnriched: TransportDetailEnriched;
};
export const TransportDetailsTransportInfos = ({
  theme,
  transportDetailEnriched,
}: TransportDetailsTransportInfosProps) => {
  const { thTransportInfos, thLuggage } = themes()[theme];
  const transportClasses = getTransportClasses(transportDetailEnriched.travelSections);
  const isTrain = transportDetailEnriched.travelSections.some((el) => el.transport.type === TRAIN);
  const hasNoBagageWeithAndQuantity =
    !transportDetailEnriched.transport.baggage.weight &&
    !transportDetailEnriched.transport.baggage.quantity;
  const baggageWeight = transportDetailEnriched.transport.baggage.weight;
  const baggageQuantity = transportDetailEnriched.transport.baggage.quantity;
  const baggageWeightUnit = transportDetailEnriched.transport.baggage.unit;
  const isCarbonEmissionsEnabled = useFeatureFlip('booking.carbonEmissions');
  const totalCarbonEmission = calculateTotalEmissions(transportDetailEnriched.travelSections);
  const hasCarbonEmission = isCarbonEmissionsEnabled && totalCarbonEmission !== null;
  return (
    <div
      className={classnames('items-center text-12', thTransportInfos)}
      data-testid="TransportDetailsTransportInfos"
    >
      <span className="mr-5">
        {isTrain ? t('common.transport.train') : t('common.transport.flight')}
        &nbsp;
        {transportDetailEnriched.stops > 0
          ? t('common.transport.connection')
          : t('common.transport.direct')}
        {transportClasses.length > 0 && ','}
      </span>
      {transportClasses.length > 0 && (
        <span className="mr-5">
          {t('common.transport.transportClass.classLabel')}
          &nbsp;
          {transportClasses
            .map((transportClass) =>
              t(`common.transport.transportClass.${TRANSPORT_CLASS_TO_I18N[transportClass]}`),
            )
            .join(', ')}
        </span>
      )}
      {!hasNoBagageWeithAndQuantity && (
        <React.Fragment>
          <div
            className={classnames(
              'rounded-full border-l border-r border-solid border-lightGrey',
              thLuggage,
            )}
          />
          <div className="flex items-center gap-4">
            <Icon
              {...makeWidgetProps<React.ComponentProps<typeof Icon>>({
                className: 'relative top-[-1px] flex items-center ps-1 text-black',
                dataTestid: 'baggage-icon',
                id: 'baggage-icon',
                name: ICONS.BAGGAGE,
                width: '17px',
              })}
            />
            <span data-testid="weightAndQuantity">
              {`${t('common.transport.weightPerPersonLabel', {
                baggageQuantity: baggageQuantity ? `(x${baggageQuantity}) ` : '',
                baggageWeight: baggageWeight
                  ? `(${baggageWeight}${Number(baggageWeight) ? baggageWeightUnit : ''}) `
                  : '',
              })}`}
            </span>
          </div>
        </React.Fragment>
      )}
      {hasCarbonEmission && totalCarbonEmission != null && (
        <React.Fragment>
          <div
            className={classnames(
              'rounded-full border-l border-r border-solid border-lightGrey',
              thLuggage,
            )}
          />
          <div className="flex items-center gap-4">
            <Icon
              {...makeWidgetProps<React.ComponentProps<typeof Icon>>({
                className: 'relative top-[-1px] flex items-center ps-1 text-black',
                dataTestid: 'carbon-emission-icon',
                id: 'carbon-emission-icon',
                name: ICONS.UNION,
                width: '17px',
              })}
            />
            <span data-testid="carbonEmission">
              {t('common.transport.carbonEmissionUnit', {
                total: totalCarbonEmission,
              })}
            </span>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
